// Armado de las filas del export de órdenes de una licitación.
//
// El archivo cumple dos funciones a la vez:
//
// 1. Las primeras 19 columnas son EXACTAMENTE las que pide el Excel de carga del
//    sistema externo, con sus nombres y en su orden. El trader borra todo lo que
//    esté a la derecha y el archivo queda listo para importar.
// 2. Desde la columna 20 en adelante van los datos internos de seguimiento
//    (adjudicación, tipo de cambio, caja), que el sistema no necesita.
//
// El orden importa de verdad: `exportRowsToXlsx` usa XLSX.utils.json_to_sheet,
// que toma los encabezados de las claves del primer objeto en orden de
// inserción. Por eso las claves se escriben en el orden del template y hay un
// test que lo verifica.

import { parseCuitDigits } from './formatters';
import { monedaBucket, montoEnMonedaIntegracion, getTipoCambio } from './licitacionesCalculator';
import { colocadoresDeLicitacion, resolverColocadorOrden } from './colocadores';

/** Encabezados del Excel de carga, en el orden exacto que espera el sistema. */
export const COLUMNAS_SISTEMA = [
  'id', 'titulo', 'valor', 'cantidad', 'cap', 'comitente', 'observaciones', 'cuit',
  'nombreCliente', 'nacionalidad', 'categoria', 'tipoPersona', 'claseOferta', 'estado',
  'tipoDocumento', 'agente', 'hora', 'integracion', 'duenoCap',
];

// Constantes que el template trae fijas en todas las filas.
const ESTADO_SISTEMA = 'VIGENTE';
const NACIONALIDAD_ARGENTINA = 200;

/**
 * Valor ofertado según lo que licita la especie: tasa o precio.
 * El tramo no competitivo no oferta ninguno de los dos.
 */
const valorOfertado = (orden, especie) => {
  if (orden.tramo === 'No Competitivo') return '';
  const valor = especie?.modalidad === 'Tasa' ? orden.tasaOfertada : orden.precioOfertado;
  return parseFloat(valor) || 0;
};

/**
 * Fila del export para una orden.
 *
 * @param {object} orden
 * @param {object} especie - especie de la licitación a la que pertenece la orden
 * @param {object} licitacion - aporta el Nro de Agente, constante en toda la subasta
 */
export const buildFilaExport = (orden, especie = {}, licitacion = {}) => {
  const pedido = parseFloat(orden.cantidadVN) || 0;
  const adjudicado = parseFloat(orden.vnAdjudicado) || 0;
  const montoEmision = parseFloat(orden.montoAdjudicado) || 0;
  const { monto: montoIntegracion, sinTipoCambio } =
    montoEnMonedaIntegracion(montoEmision, especie, orden.monedaIntegracion);
  const tipoCambio = getTipoCambio(especie);

  return {
    // --- Bloque del sistema (columnas A a S) ---
    // Nro de Colocación del instrumento en el sistema.
    id: especie.nroColocacion || '',
    titulo: especie.clase || especie.denominacion || '',
    valor: valorOfertado(orden, especie),
    cantidad: pedido,
    cap: '',
    comitente: orden.nroComitente || '',
    observaciones: orden.obs || '',
    // El sistema espera el CUIT sin guiones.
    cuit: parseCuitDigits(orden.cuit),
    nombreCliente: orden.cliente || '',
    nacionalidad: NACIONALIDAD_ARGENTINA,
    categoria: '',
    tipoPersona: '',
    claseOferta: '',
    estado: ESTADO_SISTEMA,
    tipoDocumento: orden.tipoDocumento || '',
    agente: licitacion.nroAgente || '',
    hora: '',
    // El sistema distingue solo ARS/USD, sin importar si es MEP o cable.
    integracion: monedaBucket(orden.monedaIntegracion),
    duenoCap: '',

    // --- Bloque interno de seguimiento (columna T en adelante) ---
    Especie: especie.denominacion || '',
    Modalidad: especie.modalidad || '',
    Comercial: orden.comercial || '',
    // Por cuál colocador va la orden (vacío en las licitaciones anteriores).
    Colocador: resolverColocadorOrden(orden, colocadoresDeLicitacion(licitacion))?.nombre || '',
    Tramo: orden.tramo || '',
    'VN Adjudicado': adjudicado,
    // Como porcentaje (63,3), no como fracción: la columna ya dice "%".
    '% Adjudicado': pedido > 0 ? Math.round(adjudicado / pedido * 1000) / 10 : 0,
    Moneda: orden.monedaIntegracion || '',
    'Moneda Emisión': especie.monedaEmision || '',
    'Tipo de Cambio': tipoCambio > 0 ? tipoCambio : '',
    'Monto Moneda Emisión': montoEmision,
    // Sin TC no se puede afirmar el monto en pesos: se deja explícito en vez de
    // exportar un número falso.
    'Monto a Liquidar': sinTipoCambio ? 'Falta TC' : montoIntegracion,
    'Estado Adjudicación': orden.estadoAdjudicacion || '',
    Cargada: orden.cargado ? 'Sí' : 'No',
    'Caja Hecha': orden.cajaHecha ? 'Sí' : 'No',
  };
};

/** Anchos de columna alineados con el orden de `buildFilaExport`. */
export const ANCHOS_COLUMNAS_EXPORT = [
  // Bloque del sistema
  10, 16, 12, 16, 8, 14, 24, 14, 28, 12, 12, 12, 14, 10, 14, 12, 8, 12, 10,
  // Bloque interno
  26, 10, 16, 24, 14, 16, 12, 12, 14, 14, 20, 18, 18, 10, 10,
];
